import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Photo } from './photoType';
import PhotoModal from './PhotoModal';
import { Alert } from '../components/ui/alert';

interface PhotoCarouselProps {
  photos: Photo[];
  largePhotos: Photo[];
  projectTag: 'before_project' | 'after_project';
  onDeleteClick?: (photoId: number) => Promise<void> | void;
  onError?: (message: string) => void;
}

type ToastMessage = {
  type: 'success';
  message: string;
} | null;

const PhotoCarousel: React.FC<PhotoCarouselProps> = ({ 
  photos, 
  largePhotos, 
  projectTag, 
  onDeleteClick,
  onError
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [toast, setToast] = useState<ToastMessage>(null);
  
  const filteredPhotos = photos.filter(photo => photo.project_tag === projectTag);
  const filteredLargePhotos = largePhotos.filter(photo => photo.project_tag === projectTag);

  // Compute a safe index that's always within bounds
  // This prevents "currentPhoto is undefined" errors after deletion
  const safeCurrentIndex = filteredPhotos.length > 0 
    ? Math.min(currentIndex, filteredPhotos.length - 1)
    : 0;

  // Auto-dismiss toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Reset index when photos change (sync currentIndex with safeCurrentIndex)
  useEffect(() => {
    if (currentIndex !== safeCurrentIndex) {
      setCurrentIndex(safeCurrentIndex);
    }
  }, [filteredPhotos.length, currentIndex, safeCurrentIndex]);

  const handlePhotoClick = (index: number) => {
    setCurrentIndex(index);
    setModalOpen(true);
  };

  const showSuccessToast = (message: string) => {
    setToast({ type: 'success', message });
  };

  const handleDelete = async (photoId: number) => {
    if (onDeleteClick) {
      try {
        await onDeleteClick(photoId);
        showSuccessToast('Photo deleted successfully');
      } catch (error) {
        // Propagate error to parent via onError callback
        if (onError) {
          onError(error instanceof Error ? error.message : 'Failed to delete photo');
        }
        console.error('Delete error:', error);
      }
    }
  };

  const handleModalDelete = async (largePhotoId: number) => {
    if (onDeleteClick) {
      try {
        const deletedLargePhoto = filteredLargePhotos.find(p => p.id === largePhotoId);
        if (deletedLargePhoto) {
          const smallPhotoId = filteredPhotos.find(
            p => p.image_group_key === deletedLargePhoto.image_group_key
          )?.id;
          if (smallPhotoId) {
            await onDeleteClick(smallPhotoId);
            showSuccessToast('Photo deleted successfully');
            setModalOpen(false);
          } else {
            // Could not find matching photo - propagate as error
            if (onError) {
              onError('Could not find photo to delete');
            }
            console.error('Could not find matching small photo to delete');
          }
        }
      } catch (error) {
        // Propagate error to parent via onError callback
        if (onError) {
          onError(error instanceof Error ? error.message : 'Failed to delete photo');
        }
        console.error('Delete error:', error);
      }
    }
  };

  const handlePrevious = () => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex(prev => Math.min(filteredPhotos.length - 1, prev + 1));
  };

  if (filteredPhotos.length === 0) {
    return (
      <div className="p-4 text-center text-paragraph">
        No photos available. You can upload photos by clicking the Upload Photos button.
      </div>
    );
  }

  // Use safeCurrentIndex instead of currentIndex for accessing the array
  const currentPhoto = filteredPhotos[safeCurrentIndex];
  const hasPrevious = safeCurrentIndex > 0;
  const hasNext = safeCurrentIndex < filteredPhotos.length - 1;

  return (
    <div className="w-full max-w-[600px] mx-auto relative">
      {/* Success Toast Notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-[10001] animate-in slide-in-from-top-2 duration-300">
          <Alert 
            variant="success"
            className="shadow-lg min-w-[280px] sm:min-w-[320px]"
          >
            {toast.message}
          </Alert>
        </div>
      )}

      {/* Photo Display */}
      <div className="relative w-full overflow-hidden bg-page-gray-soft rounded-lg">
        <div
          className="relative cursor-pointer"
          onClick={() => handlePhotoClick(safeCurrentIndex)}
        >
          <img
            src={currentPhoto.url}
            alt={currentPhoto.file_name}
            className="w-full h-[200px] object-contain transition-opacity duration-300"
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleDelete(currentPhoto.id);
            }}
            className="absolute bottom-2 right-2 size-6 rounded-full bg-white/90 border border-muted flex items-center justify-center cursor-pointer p-0 z-[2] hover:bg-white transition-colors"
            aria-label="Delete photo"
          >
            <X className="size-4 text-foreground" />
          </button>
        </div>
      </div>

      {/* Navigation Controls - Only show if more than 1 photo */}
      {filteredPhotos.length > 1 && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <button
            onClick={handlePrevious}
            disabled={!hasPrevious}
            className="flex items-center justify-center size-10 rounded-full bg-primary text-white hover:bg-primary-hover transition-colors disabled:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Previous photo"
          >
            <ChevronLeft className="size-5" />
          </button>
          
          <span className="text-sm text-paragraph font-medium min-w-[60px] text-center">
            {safeCurrentIndex + 1} / {filteredPhotos.length}
          </span>
          
          <button
            onClick={handleNext}
            disabled={!hasNext}
            className="flex items-center justify-center size-10 rounded-full bg-primary text-white hover:bg-primary-hover transition-colors disabled:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Next photo"
          >
            <ChevronRight className="size-5" />
          </button>
        </div>
      )}

      <PhotoModal
        photos={filteredLargePhotos}
        currentPhotoIndex={safeCurrentIndex}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onNext={() => setCurrentIndex(prev => Math.min(prev + 1, filteredLargePhotos.length - 1))}
        onPrevious={() => setCurrentIndex(prev => Math.max(prev - 1, 0))}
        onDeleteClick={handleModalDelete}
      />
    </div>
  );
};

export default PhotoCarousel;